import React, { useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, UtensilsCrossed, Settings, MessageSquare, Users, LogOut, Loader2, Bell, Images } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/alerts", label: "Order Alerts", icon: Bell },
  { to: "/admin/gallery", label: "Event Gallery", icon: Images },
  { to: "/admin/orders", label: "Orders", icon: ShoppingBag },
  { to: "/admin/menu", label: "Menu Manager", icon: UtensilsCrossed },
  { to: "/admin/enquiries", label: "Enquiries", icon: MessageSquare },
  { to: "/admin/settings", label: "Site Settings", icon: Settings },
  { to: "/admin/users", label: "Users", icon: Users, superOnly: true },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  // FIX 5: force a password change — block every admin page except Site Settings
  // until the temporary password has been replaced.
  useEffect(() => {
    if (user && user !== false && user.must_change_password && location.pathname !== "/admin/settings") {
      navigate("/admin/settings", { replace: true });
    }
  }, [user, location.pathname, navigate]);

  if (user === null || user === false) {
    return <div className="min-h-screen flex items-center justify-center bg-sapore-cream"><Loader2 className="animate-spin text-sapore-purple" /></div>;
  }

  return (
    <div className="min-h-screen flex bg-sapore-cream">
      <aside className="w-16 sm:w-60 bg-sapore-purple text-sapore-cream flex flex-col flex-shrink-0">
        <div className="p-4 border-b border-white/10 flex items-center justify-center sm:justify-start">
          <img src="/assets/sapore-favicon.png" alt="Sapore" className="h-9 w-9 rounded-lg" />
          <span className="hidden sm:block ml-2 font-serif text-lg">Sapore Admin</span>
        </div>
        <nav className="flex-1 p-2 sm:p-3 space-y-1">
          {LINKS.filter((l) => !l.superOnly || user.role === "super_admin").map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`admin-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                  isActive ? "bg-sapore-gold text-sapore-ink" : "text-sapore-cream/80 hover:bg-white/10"
                }`
              }
            >
              <l.icon size={18} /> <span className="hidden sm:block">{l.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-2 sm:p-3 border-t border-white/10">
          <div className="hidden sm:block px-3 py-2 text-xs text-sapore-cream/60 truncate">{user.email}</div>
          <button data-testid="admin-logout" onClick={() => { logout(); navigate("/admin/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-sapore-cream/80 hover:bg-white/10">
            <LogOut size={18} /> <span className="hidden sm:block">Logout</span>
          </button>
        </div>
      </aside>
      <div className="flex-1 overflow-x-hidden">
        {user.must_change_password && (
          <div className="bg-sapore-gold/90 text-sapore-ink text-sm px-4 py-2 text-center">
            You're using a temporary password. Please change it in Site Settings.
          </div>
        )}
        <div className="p-4 sm:p-8"><Outlet /></div>
      </div>
    </div>
  );
}
